import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Inbox, MessagesSquare, Kanban, Building2, Package, Home, Settings, ChevronsLeft, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/common";
import { LosMark } from "@/components/Logo";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UserMenu, ROLE_LABEL } from "@/components/layout/UserMenu";
import { useResource } from "@/hooks/use-resource";
import { RAIL } from "@/lib/layout";

/** Main modules. `match` decides which module a path belongs to (drives both the rail and the context panel). */
export const MODULES = [
  { key: "oversikt", to: "/", label: "Hjem", icon: Home, kbd: "1", match: (p) => p === "/", testId: "rail-home" },
  { key: "work", to: "/arbeid", label: "Arbeid", icon: MessagesSquare, kbd: "2", match: (p) => p === "/ny" || p.startsWith("/ny/") || p.startsWith("/p/") || p.startsWith("/arbeid"), testId: "rail-work" },
  { key: "inbox", to: "/innboks", label: "Innboks", icon: Inbox, kbd: "3", match: (p) => p.startsWith("/innboks"), badge: true, testId: "rail-inbox" },
  { key: "pipeline", to: "/pipeline", label: "Tilbudsoversikt", icon: Kanban, kbd: "4", match: (p) => p.startsWith("/pipeline"), testId: "rail-pipeline" },
  { key: "customers", to: "/customers", label: "Kunder", icon: Building2, kbd: "5", match: (p) => p.startsWith("/customers"), testId: "rail-customers" },
  { key: "products", to: "/products", label: "Produkter", icon: Package, kbd: "6", match: (p) => p.startsWith("/products"), testId: "rail-products" },
];
export const SETTINGS = { key: "settings", to: "/settings", label: "Innstillinger", icon: Settings, kbd: ",", match: (p) => p.startsWith("/settings"), testId: "rail-settings" };

export const moduleFor = (pathname) => MODULES.find((m) => m.match(pathname)) || (SETTINGS.match(pathname) ? SETTINGS : MODULES[0]);

/** Tooltip only while the rail is compact – expanded rows carry their own label. */
function Tip({ label, kbd, off, children }) {
  if (off) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={10} className="flex items-center gap-2 rounded-lg px-2.5 py-1 text-[12px]">
        {label}
        {kbd ? <span className="kbd">⌘{kbd}</span> : null}
      </TooltipContent>
    </Tooltip>
  );
}

const Badge = ({ n, testId, inline }) => (
  <span className={cn("flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 font-mono text-[10px] font-semibold tabular-nums text-white", inline ? "ml-auto" : "absolute -right-0.5 -top-0.5 ring-2 ring-[hsl(var(--sidebar))]")} data-testid={testId}>{n > 99 ? "99+" : n}</span>
);

function RailItem({ m, active, badge, expanded, index }) {
  const Icon = m.icon;
  return (
    <Tip label={m.label} kbd={m.kbd} off={expanded}>
      <Link to={m.to} aria-label={m.label} aria-current={active ? "page" : undefined} data-active={active ? "1" : "0"} data-testid={m.testId}
        className={cn("group relative flex h-11 items-center rounded-[12px] outline-none transition-[background-color,box-shadow,transform,color] duration-150 focus-visible:ring-2 focus-visible:ring-ring/40 active:scale-[0.97]",
          expanded ? "h-10 w-full gap-3 px-2.5" : "w-11 justify-center",
          active ? "bg-white text-[hsl(var(--brand-strong))] shadow-[0_1px_2px_hsl(var(--shadow-color)/0.1),0_0_0_1px_hsl(var(--shadow-color)/0.07)]" : "text-foreground/60 hover:bg-black/[0.06] hover:text-foreground")}>
        <Icon className="h-[22px] w-[22px] shrink-0 transition-transform duration-150 group-hover:scale-[1.04]" strokeWidth={active ? 2.1 : 1.8} />
        {expanded ? (
          <>
            <span className={cn("rail-label min-w-0 flex-1 truncate text-left text-[13.5px]", active ? "font-semibold" : "font-medium")} style={{ animationDelay: `${index * 18}ms` }}>{m.label}</span>
            {badge ? <Badge n={badge} inline testId={`${m.testId}-badge`} /> : <span className="kbd opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden="true">⌘{m.kbd}</span>}
          </>
        ) : badge ? <Badge n={badge} testId={`${m.testId}-badge`} /> : null}
      </Link>
    </Tip>
  );
}

/** The Skarpenord mark is the switch: compact icon rail ↔ rail with module names. */
function Brand({ expanded, onToggle }) {
  return (
    <Tip label={expanded ? "Skjul modulnavn" : "Vis modulnavn"} off={expanded}>
      <button type="button" onClick={onToggle} aria-expanded={expanded} aria-controls="rail-modules" aria-label={expanded ? "Skarpenord – skjul modulnavn" : "Skarpenord – vis modulnavn"} data-testid="rail-logo"
        className={cn("group flex h-11 items-center rounded-[12px] outline-none transition-[background-color,transform] duration-150 focus-visible:ring-2 focus-visible:ring-ring/40 active:scale-[0.98]", expanded ? "h-10 w-full gap-3 px-1.5 hover:bg-black/[0.05]" : "w-11 justify-center hover:scale-[1.04]")}>
        <LosMark className="h-[30px] w-[30px] shrink-0 transition-transform duration-200 group-hover:rotate-[-3deg]" />
        {expanded ? (
          <>
            <span className="rail-label min-w-0 flex-1 truncate text-left font-display text-[15px] font-bold tracking-[-0.02em] text-[hsl(var(--navy))]">Skarpenord</span>
            <ChevronsLeft className="rail-label h-4 w-4 shrink-0 text-foreground/35 transition-colors group-hover:text-foreground/70" strokeWidth={1.8} aria-hidden="true" />
          </>
        ) : null}
      </button>
    </Tip>
  );
}

function You({ expanded }) {
  const { user } = useAuth();
  return (
    <UserMenu align="end" side={expanded ? "top" : "right"} testId="user-menu">
      <button type="button" aria-label="Profil og meny" data-testid="user-menu-trigger"
        className={cn("group flex h-11 items-center rounded-[12px] outline-none transition-colors hover:bg-black/[0.05] focus-visible:ring-2 focus-visible:ring-ring/40 data-[state=open]:bg-black/[0.05]", expanded ? "h-10 w-full gap-3 px-1" : "w-11 justify-center")}>
        <UserAvatar name={user?.name} initials={user?.initials} color={user?.color} src={user?.avatar_url} size="md" className="h-[34px] w-[34px] shrink-0" testId="rail-user-avatar" />
        {expanded ? (
          <>
            <span className="rail-label min-w-0 flex-1 text-left leading-tight">
              <span className="block truncate text-[13px] font-semibold text-foreground" data-testid="rail-user-name">{user?.name}</span>
              <span className="block truncate text-[11px] text-muted-foreground">{user?.title || ROLE_LABEL[user?.role] || user?.role}</span>
            </span>
            <ChevronsUpDown className="rail-label h-3.5 w-3.5 shrink-0 text-foreground/35" strokeWidth={1.8} aria-hidden="true" />
          </>
        ) : null}
      </button>
    </UserMenu>
  );
}

/** Module rail: brand → modules → settings/you. Compact (64 px) by default; the LOS mark expands it to show the names. */
export function Rail({ expanded = false, onToggle, className }) {
  const { pathname } = useLocation();
  const inbox = useResource("/inbox", null, { interval: 45000 });
  const active = moduleFor(pathname);
  return (
    <nav className={cn("relative flex h-full shrink-0 flex-col bg-[hsl(var(--sidebar))] py-2.5 transition-[width] duration-200 ease-[cubic-bezier(0.2,0.7,0.2,1)]", expanded ? "px-2.5" : "items-center", className)}
      style={{ width: expanded ? RAIL.expanded : RAIL.compact }} aria-label="Moduler" data-testid="rail" data-expanded={expanded ? "1" : "0"}>
      {expanded ? <span className="pointer-events-none absolute inset-y-3 right-0 w-px bg-black/[0.06]" aria-hidden="true" /> : null}
      <Brand expanded={expanded} onToggle={onToggle} />
      <div id="rail-modules" className={cn("mt-3 flex flex-col gap-1", !expanded && "items-center")}>
        {MODULES.map((m, i) => <RailItem key={m.key} m={m} index={i} expanded={expanded} active={active.key === m.key} badge={m.badge ? inbox.data?.unread || 0 : 0} />)}
      </div>
      <div className={cn("mt-auto flex flex-col gap-1.5", !expanded && "items-center")}>
        <RailItem m={SETTINGS} index={MODULES.length} expanded={expanded} active={active.key === "settings"} />
        <You expanded={expanded} />
      </div>
    </nav>
  );
}
